/** Combines built-in and personalised categories/symbols for display. */
import type { Language } from '@shared/constants';
import type { CustomCategory, CustomSymbol } from '@shared/schemas';
import { BASE_CATEGORIES, BASE_SYMBOLS, type BaseSymbol } from '@shared/symbols';
import type { PictoSource } from './picto';

export interface DisplayCategory {
  id: string;
  name: string;
  icon: string;
  /** Bundled image for built-in categories; custom ones show the emoji. */
  image?: string;
  color: string;
  textColor: string;
  custom: boolean;
}

export interface DisplaySymbol {
  id: string;
  categoryId: string;
  label: string;
  picto: PictoSource;
  custom: boolean;
  /** A built-in symbol whose name or picture was changed (e.g. a family photo on "Mamãe"). */
  edited?: boolean;
}

const BASE_IDS = new Set(BASE_SYMBOLS.map((s) => s.id));

/**
 * Personalised entries that share a built-in symbol's id are not new symbols:
 * they change that symbol's name and/or picture (e.g. a photo of mum on "Mamãe").
 */
export function isBaseOverride(symbol: CustomSymbol): boolean {
  return BASE_IDS.has(symbol.id);
}

export function findBaseSymbol(id: string): BaseSymbol | undefined {
  return BASE_SYMBOLS.find((s) => s.id === id);
}

function baseDisplay(base: BaseSymbol, override: CustomSymbol | undefined, language: Language): DisplaySymbol {
  return {
    id: base.id,
    categoryId: base.categoryId,
    label: override ? override.text[language] : base.text[language],
    picto: { icon: override?.icon ?? base.icon, image: override?.image ?? base.image },
    custom: false,
    edited: !!override,
  };
}

export function displayCategories(custom: CustomCategory[], language: Language): DisplayCategory[] {
  return [
    ...BASE_CATEGORIES.map((c) => ({
      id: c.id,
      name: c.name[language],
      icon: c.icon,
      image: c.image,
      color: c.color,
      textColor: c.textColor,
      custom: false,
    })),
    // Custom colours are only used as accents; text stays dark for contrast.
    ...custom.map((c) => ({ id: c.id, name: c.name, icon: c.icon, color: c.color, textColor: '#1f2937', custom: true })),
  ];
}

export function displaySymbolsFor(categoryId: string, custom: CustomSymbol[], language: Language): DisplaySymbol[] {
  const base = BASE_SYMBOLS.filter((s) => s.categoryId === categoryId).map((s) =>
    baseDisplay(s, custom.find((c) => c.id === s.id), language),
  );
  const own = custom.filter((s) => s.categoryId === categoryId && !isBaseOverride(s)).map((s) => toDisplay(s, language));
  return [...base, ...own];
}

export function toDisplay(s: CustomSymbol, language: Language): DisplaySymbol {
  return {
    id: s.id,
    categoryId: s.categoryId,
    label: s.text[language],
    picto: { icon: s.icon, image: s.image ?? null },
    custom: true,
  };
}

export function findDisplaySymbol(id: string, custom: CustomSymbol[], language: Language): DisplaySymbol | null {
  const base = findBaseSymbol(id);
  if (base) return baseDisplay(base, custom.find((c) => c.id === id), language);
  const own = custom.find((s) => s.id === id);
  return own ? toDisplay(own, language) : null;
}

/** Moves the item at `index` by `delta` positions; returns a new array. */
export function move<T>(items: T[], index: number, delta: number): T[] {
  const target = index + delta;
  if (index < 0 || index >= items.length || target < 0 || target >= items.length) return items;
  const copy = [...items];
  const [item] = copy.splice(index, 1);
  copy.splice(target, 0, item!);
  return copy;
}
